import styled from '@emotion/styled'
import AppCommandBar from 'app/AppCommandBar'
import { CalendarContext } from 'components/AppBar'
import { StickyPane } from 'components/StickyPane'
import { ImportProvingDialog } from 'dialogs/ImportProvingDialog'
import { FormikActions } from 'formik'
import {
    CreateProvingForm,
    CreateProvingFormSchema,
} from 'forms/CreateProvingForm'
import { Stack } from 'forms/FormStack'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand, useCreateProvingCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useNewNewsContext } from 'hooks/useNewNewsContext'
// import { useNewWikiContext } from 'hooks/useNewWikiContext'
import { useRouteParams } from 'hooks/useRouteParams'
import { Proving, ProvingInsertType, UserRole } from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useReadMyProvingsQuery } from 'views/MyProvingIndexView/MyProvingsIndexView.generated'
import { useCreateProvingMutation } from 'views/ProvingsIndexView/ProvingsIndexView.generated'
import { HOMEPAGE_ITEM_TYPE, MenuItemView } from './MenuItemView'
import './homeScreen.css'

// const GridContainer = styled.div`
//   display: grid;
//   grid-template-columns: 62vw 9vw;
//   grid-template-rows: 70vh 18vh;
//   grid-template-areas: 'Main Side' 'Admin .';
//   column-gap: 15vw;
//   row-gap: 1%;
//   padding: 1%;
// `

const Main = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-area: Main;
  gap: 1%;
  padding: 1vh;
`

const Admin = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-area: Admin;
  gap: 1%;
  padding: 1%;
`

const Side = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-area: Side;
  gap: 1%;
  padding: 1vh;
`

interface IMenuItem {
  routerPath: string
  itemName: string
  translation: string
  iconName: string
}

export const HomeScreen: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [routeParams] = useRouteParams<{
    search?: string
  }>('provings', {
    search: undefined,
  })
  const auth = useAuthContext()
  const modifiedNewsCounter = useNewNewsContext()
  // const modifiedWikisCounter = useNewWikiContext()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isUser = auth.hasUserRole(UserRole.User)
  const isProvingCreator = auth.hasUserRole(UserRole.ProvingCreator)
  const isHRG = auth.hasUserRole(UserRole.Hrg)
  const isReader = auth.hasUserRole(UserRole.Reader)
  const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
  const isMaintenanceHead = auth.hasUserRole(UserRole.Maintenancehead)
  const isServiceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const isProvingOrganisation = auth.hasUserRole(UserRole.ProvingOrganisation)
  const [panelState, showPanel, hidePanel] =
    useFormikPanelState<ProvingInsertType>()
  const [createProving] = useCreateProvingMutation()

  const [showImport, setShowImport] = React.useState(false)

  useCommand(
    {
      key: 'provings.csvImport',
      text: t('CSV-Import'),
      title: t('CSV-Import'),
      iconProps: { iconName: 'Upload' },
      priority: 15,
      onClick: () => setShowImport(true),
      hidden: !isAdmin,
    },
    [t, isAdmin]
  )

  const { refetch } = useReadMyProvingsQuery({
    variables: {
      search: routeParams.search,
    },
  })

  const showNewProving = React.useCallback(
    (proving: Partial<Proving>) => {
      router.navigate('provings.details', {
        id: proving.id,
        page: 'participants',
      })
    },
    [router]
  )
  useCreateProvingCommand(10, () => showPanel({} as ProvingInsertType))
  const handleSubmit = async (
    values: ProvingInsertType,
    formikActions: FormikActions<ProvingInsertType>
  ) => {
    formikActions.setSubmitting(true)

    const createdProvingJSON = await createProving({
      variables: { input: values },
    })
    const newProvingJson = createdProvingJSON as {
      data: { createProving: { id: string } }
    }
    const newProving: Partial<Proving> = {
      id: newProvingJson.data.createProving.id,
    }
    await refetch({
      search: routeParams.search,
    })
    formikActions.setSubmitting(false)
    hidePanel()
    showNewProving(newProving)
  }
  // TODO:CALENDAR LINKS
  const allMenuElements: IMenuItem[] = [
    {
      itemName: 'myBookings',
      iconName: 'ThumbnailView',
      translation: 'Meine Buchungen',
      routerPath: 'myBookings',
    },
    {
      itemName: 'myProvings',
      iconName: 'TestBeaker',
      translation: 'Meine Erprobungen',
      routerPath: 'myprovings',
    },
    {
      itemName: 'todos',
      iconName: 'Globe',
      translation: 'Meine ToDos',
      routerPath: 'todos',
    },
    {
      itemName: 'synergie',
      iconName: 'Sync',
      translation: 'Synergiesuche',
      routerPath: 'synergies',
    },
    {
      itemName: 'mailbox',
      iconName: 'Inbox',
      translation: 'Mailbox',
      routerPath: 'mailbox',
    },
    {
      itemName: 'assignedWorker',
      iconName: 'WorkforceManagement',
      translation: 'Zuordnung Werkstattmitarbeiter',
      routerPath: 'assignedWorker',
    },
    // {
    //   itemName: 'byMaintenanceHead',
    //   iconName: 'ManagerSelfService',
    //   translation: 'Erprobungen nach Meister',
    //   routerPath: 'byMaintenanceHead',
    // },
  ]
  const readerMenuElements: IMenuItem[] = [
    {
      itemName: 'myBookings',
      iconName: 'ThumbnailView',
      translation: 'Meine Buchungen',
      routerPath: 'myBookings',
    },
    {
      itemName: 'myProvings',
      iconName: 'TestBeaker',
      translation: 'Meine Erprobungen',
      routerPath: 'myprovings',
    },
    {
      itemName: 'todos',
      iconName: 'Globe',
      translation: 'Meine ToDos',
      routerPath: 'todos',
    },
    {
      itemName: 'synergie',
      iconName: 'Sync',
      translation: 'Synergiesuche',
      routerPath: 'synergies',
    },
    {
      itemName: 'assignedWorker',
      iconName: 'WorkforceManagement',
      translation: 'Zuordnung Werkstattmitarbeiter',
      routerPath: 'assignedWorker',
    },
    // {
    //   itemName: 'byMaintenanceHead',
    //   iconName: 'ManagerSelfService',
    //   translation: 'Erprobungen nach Meister',
    //   routerPath: 'byMaintenanceHead',
    // },
  ]
  const userMenuElements: IMenuItem[] = [
    {
      itemName: 'myBookings',
      iconName: 'ThumbnailView',
      translation: 'Meine Buchungen',
      routerPath: 'myBookings',
    },
    {
      itemName: 'myProvings',
      iconName: 'TestBeaker',
      translation: 'Meine Erprobungen',
      routerPath: 'myprovings',
    },
    {
      itemName: 'todos',
      iconName: 'Globe',
      translation: 'Meine ToDos',
      routerPath: 'todos',
    },
    {
      itemName: 'synergie',
      iconName: 'Sync',
      translation: 'Synergiesuche',
      routerPath: 'synergies',
    },
  ]
  const hrgMenuElements: IMenuItem[] = [
    {
      itemName: 'todos',
      iconName: 'Globe',
      translation: 'Meine ToDos',
      routerPath: 'todos',
    },
    {
      itemName: 'mailbox',
      iconName: 'Inbox',
      translation: 'Mailbox',
      routerPath: 'mailbox',
    },
  ]

  const maintenanceHeadMenuElements: IMenuItem[] = [
    {
      itemName: 'myBookings',
      iconName: 'ThumbnailView',
      translation: 'Meine Buchungen',
      routerPath: 'myBookings',
    },
    {
      itemName: 'myProvings',
      iconName: 'TestBeaker',
      translation: 'Meine Erprobungen',
      routerPath: 'myprovings',
    },
    {
      itemName: 'todos',
      iconName: 'Globe',
      translation: 'Meine ToDos',
      routerPath: 'todos',
    },
    {
      itemName: 'synergie',
      iconName: 'Sync',
      translation: 'Synergiesuche',
      routerPath: 'synergies',
    },
    {
      itemName: 'assignedWorker',
      iconName: 'WorkforceManagement',
      translation: 'Zuordnung Werkstattmitarbeiter',
      routerPath: 'assignedWorker',
    },
    // {
    //   itemName: 'byMaintenanceHead',
    //   iconName: 'ManagerSelfService',
    //   translation: 'Erprobungen nach Meister',
    //   routerPath: 'byMaintenanceHead',
    // },
  ]

  const adminMenuElements: IMenuItem[] = [
    {
      itemName: 'adminProvingGrounds',
      iconName: 'PhotoCollection',
      translation: 'Standorte',
      routerPath: 'admin.data.provingGrounds',
    },
    {
      itemName: 'adminRes',
      iconName: 'BuildDefinition',
      translation: 'Ressourcen',
      routerPath: 'admin.data.resources',
    },
    {
      itemName: 'adminWorkers',
      iconName: 'ProfileSearch',
      translation: 'Werkstattmitarbeiter',
      routerPath: 'admin.data.workers',
    },
    {
      itemName: 'adminFileTemplates',
      iconName: 'FileTemplate',
      translation: 'Dateivorlagen',
      routerPath: 'admin.data.fileTemplates',
    },
    {
      itemName: 'adminMailTemplates',
      iconName: 'MailOptions',
      translation: 'E-Mail Vorlagen',
      routerPath: 'admin.data.mailTemplates',
    },
  ]

  const noMenuElements: IMenuItem[] = []

  const hrgSideMenuElements: IMenuItem[] = [
    {
      itemName: 'wiki',
      iconName: 'D365TalentInsight',
      translation: 'Wiki',
      routerPath: 'wiki',
    },
  ]

  const sideMenuElements: IMenuItem[] = [
    {
      itemName: 'wiki',
      iconName: 'D365TalentInsight',
      translation: 'Wiki',
      routerPath: 'wiki',
    },
    {
      itemName: 'news',
      iconName: 'Globe',
      translation: 'Neuigkeiten',
      routerPath: 'news',
    },

    // {
    //   itemName: 'calendar',
    //   iconName: 'CalendarWeek',
    //   translation: 'Kalender',
    //   routerPath: '',
    // },
  ]

  const generalMenuElements = (isUser || isProvingCreator)
    ? userMenuElements
    : isHRG
    ? hrgMenuElements
    : isReader
    ? readerMenuElements
    : isMaintenanceHead
    ? maintenanceHeadMenuElements
    : isAdmin
    ? allMenuElements
    : isResourceManager
    ? allMenuElements
    : isServiceTeam
    ? allMenuElements
    : isProvingOrganisation
    ? allMenuElements
    : noMenuElements

  const generalSideMenuElements = isHRG ? hrgSideMenuElements : sideMenuElements

  const calendarToggler: {
    toggleCalendarDisplay: React.Dispatch<React.SetStateAction<boolean>>
  } = useContext(CalendarContext)

  const onClickHandler = (name: string, fullPath: string) => {
    if (name === 'calendar') {
      calendarToggler.toggleCalendarDisplay(true)
    } else if (name === 'wiki' && !window.location.href.includes('_admin')) {
      window.open(
        'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
        '_blank',
        'toolbar=0,location=0,menubar=0'
      )
    } else {
      // router.navigate(fullPath,{newsCounter:newsCounterCurrent.current,wikiCounter:wikisCounterCurrent.current})
      router.navigate(fullPath, {
        newsCounter: 0,
        wikiCounter: 0,
      })
    }
  }

  return (
    <>
      <StickyPane>
        <AppCommandBar />
          {isAdmin && (
            <ImportProvingDialog
              visible={showImport}
              onClose={() => setShowImport(false)}
            />
          )}
        <FormikPanel
          {...panelState}
          onSubmit={handleSubmit}
          headerText={t('model.proving.create')}
          validationSchema={new CreateProvingFormSchema()}
        >
          <CreateProvingForm />
        </FormikPanel>

        <Stack
          horizontal={true}
          horizontalAlign="space-between"
          styles={{ root: { height: '94% !important' } }}
        >
          <Stack verticalAlign="space-between">
            <Stack.Item>
              <Main>
                {generalMenuElements.map((menuElement) => (
                  <MenuItemView
                    {...menuElement}
                    clickHandler={onClickHandler}
                    type={HOMEPAGE_ITEM_TYPE.big}
                    key={`homepageMain${menuElement.itemName}`}
                  />
                ))}
              </Main>
            </Stack.Item>
            <Stack.Item>
              <Admin>
                {isAdmin &&
                  adminMenuElements.map((menuElement) => (
                    <MenuItemView
                      {...menuElement}
                      clickHandler={onClickHandler}
                      type={HOMEPAGE_ITEM_TYPE.small}
                      key={`homepageAdmin${menuElement.itemName}`}
                    />
                  ))}
              </Admin>
            </Stack.Item>
          </Stack>
          <Stack>
            <Side>
              {generalSideMenuElements.map((menuElement) => {
                // const notificationProps: { notificationCounter?: number } = {}
                // if (menuElement.itemName === 'news') {
                //   notificationProps.notificationCounter =
                //     modifiedNewsCounter.newNewsCount
                // newsCounterCurrent.current=modifiedNewsCounter.newNewsCount
                // }
                // if (menuElement.itemName === 'wiki') {
                //   notificationProps.notificationCounter =
                //     modifiedWikisCounter.newWikiCount
                //   // wikisCounterCurrent.current=modifiedWikisCounter.newWikiCount
                // }
                return (
                  <MenuItemView
                    {...menuElement}
                    notificationCounter={0}
                    clickHandler={onClickHandler}
                    type={HOMEPAGE_ITEM_TYPE.small}
                    key={`homepageSide${menuElement.itemName}`}
                  />
                )
              })}
            </Side>
          </Stack>
        </Stack>

        {/* <Stack verticalAlign="space-between" styles={{ root: { height: '95%' } }}>
        <Stack horizontalAlign="start">
          <Main>
            {generalMenuElements.map(menuElement => (
              <MenuItemView
                {...menuElement}
                clickHandler={onClickHandler}
                type={HOMEPAGE_ITEM_TYPE.big}
                key={`homepageMain${menuElement.itemName}`}
              />
            ))}
          </Main>
        </Stack>
        <Stack verticalAlign="end">
          <Admin>
            {isAdmin &&
              adminMenuElements.map(menuElement => (
                <MenuItemView
                  {...menuElement}
                  clickHandler={onClickHandler}
                  type={HOMEPAGE_ITEM_TYPE.small}
                  key={`homepageAdmin${menuElement.itemName}`}
                />
              ))}
          </Admin>
        </Stack>
      </Stack> */}
      </StickyPane>
    </>
  )
}
